/**
 * Trois familles de statut, jamais fondues en une seule liste de couleurs.
 *
 * Un statut métier (cycle de vie de l'entité), un état financier et une alerte
 * opérationnelle décrivent trois faits distincts : un Contrat actif peut porter
 * une Facture en retard, et une alerte n'est pas un état permanent. Les fondre
 * dans un seul jeu de tons revient à laisser chaque page réinventer la
 * correspondance — et à laisser diverger deux badges du même fait.
 *
 * Une page passe une valeur de famille, jamais une couleur : le ton est résolu
 * ici, une fois.
 */
import type { StatusTone } from './StatusBadge'

/** Cycle de vie d'une entité : Client, Contrat, Soumission, Lead. */
export type BusinessStatusValue =
  | 'draft'
  | 'pending'
  | 'active'
  | 'suspended'
  | 'expired'
  | 'cancelled'
  | 'archived'
  | 'inactive'
  | 'awaitingSignature'
  | 'awaitingPayment'
  | 'awaitingSignatureOpened'
  | 'contractSuspended'
  | 'contractCancelled'
  | 'notInterested'
  | 'followUp'
  | 'won'
  | 'lost'

/** État financier, indépendant du statut métier de l'entité qui le porte. */
export type FinancialStatusValue =
  'draft' | 'ready' | 'issued' | 'sent' | 'partiallyPaid' | 'paid' | 'overdue' | 'cancelled' | 'pending'

/** Balises d'un contrat : à installer (jaune) puis en place (vert). */
export type MarkerStatusValue = 'required' | 'inPlace'

/** Signalement temporaire, jamais un état permanent de l'entité. */
export type AlertStatusValue = 'critical' | 'warning' | 'info'

export const businessStatusTone: Record<BusinessStatusValue, StatusTone> = {
  draft: 'neutral',
  pending: 'info',
  active: 'success',
  suspended: 'warning',
  expired: 'danger',
  cancelled: 'danger',
  archived: 'neutral',
  inactive: 'neutral',
  // Violet distinct de `awaitingPayment` (ambre) — audit UX/UI 16 sept 2026
  // §6.3 : « Envoyé / attente de signature » et « Paiement requis » se
  // confondaient auparavant, tous deux en `warning`.
  awaitingSignature: 'pending',
  awaitingPayment: 'warning',
  // Contrats (7 oct 2026) : Ouvert = violet, Suspendu = rouge clair, Annulé = gris pâle,
  // Pas intéressé = gris foncé. Expiré garde `expired` (rouge foncé).
  awaitingSignatureOpened: 'pending',
  contractSuspended: 'dangerLight',
  contractCancelled: 'neutralLight',
  notInterested: 'neutralStrong',
  followUp: 'pending',
  won: 'success',
  lost: 'danger',
}

export const financialStatusTone: Record<FinancialStatusValue, StatusTone> = {
  draft: 'neutral',
  ready: 'info',
  issued: 'info',
  sent: 'info',
  partiallyPaid: 'warning',
  paid: 'success',
  overdue: 'danger',
  cancelled: 'danger',
  pending: 'warning',
}

export const markerStatusTone: Record<MarkerStatusValue, StatusTone> = {
  required: 'warning',
  inPlace: 'success',
}

export const alertStatusTone: Record<AlertStatusValue, StatusTone> = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
}
